import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { AssetPreview } from "../../components/AssetPreview";
import { useTenantAuth } from "../../context/auth";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";
import { normalizeLocale, SUPPORTED_LOCALES, type SupportedLocale } from "../../lib/locales";
import { renderSafeMarkdown } from "../../lib/markdown";

type Customer = {
  id: string;
  full_name: string;
  phone?: string;
  email?: string;
  branch_id?: string;
  locale?: SupportedLocale;
  telegram_chat_id?: string | null;
  telegram_booking_mode?: "web_page" | "web_app" | "both";
  risk_score: number;
  risk_tier: string;
  visits_total: number;
  completed_total: number;
  cancelled_total: number;
  no_show_total: number;
  is_archived?: boolean;
};

type ConversationItem = {
  id: string;
  kind: string;
  channel: string;
  direction?: string | null;
  event_type?: string | null;
  status?: string | null;
  delivery_error?: string | null;
  integration_id?: string | null;
  integration_name?: string | null;
  appointment_id?: string | null;
  body_markdown?: string | null;
  body_text?: string | null;
  media: Array<{ asset_id?: string; mime_type?: string; filename?: string }>;
  meta: Record<string, unknown>;
  actor_user_id?: string | null;
  created_at: string;
};

type UploadOut = { id: string };

function humanizeSendError(err: unknown, phone?: string): string {
  const raw = String(err ?? "");
  if (raw.includes("customer_chat_id_missing")) {
    const phoneHint = phone || "CLIENT_PHONE";
    return `Client is not linked to Telegram yet. Ask them to send /start and /register ${phoneHint} to your Telegram bot.`;
  }
  if (raw.includes("integration_not_found")) return "Telegram integration is not configured or disabled.";
  if (raw.includes("unsupported_channel")) return "Only Telegram sending is supported right now.";
  if (raw.includes("telegram_send_failed")) return "Telegram delivery failed. Check bot token/webhook and retry.";
  return raw;
}

function humanizeDeliveryError(code?: string | null): string | null {
  if (!code) return null;
  if (code === "customer_chat_id_missing") return "Client is not linked to Telegram.";
  if (code === "integration_not_found") return "Telegram integration not found.";
  if (code === "unsupported_channel") return "Only Telegram sending is supported.";
  if (code === "telegram_send_failed") return "Telegram delivery failed.";
  if (code === "asset_file_missing") return "Attachment file missing on server.";
  return code;
}

function humanizeBookingMode(mode?: string | null): string {
  if (mode === "web_app") return "web_app";
  if (mode === "both") return "both";
  return "web_page";
}

export function ManagerClientsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { bootstrap, token } = useTenantAuth();
  const { t } = useTranslation();
  const tr = (key: string, fallback: string, options?: Record<string, unknown>) => {
    const translated = t(key, { ...options, defaultValue: fallback });
    return translated === key ? fallback : translated;
  };
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState("");
  const [listMode, setListMode] = useState<"active" | "archived">("active");
  const [riskFilter, setRiskFilter] = useState<"all" | "minimal" | "low" | "medium" | "high">("all");
  const [telegramFilter, setTelegramFilter] = useState<"all" | "linked" | "unlinked">("all");

  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [branchId, setBranchId] = useState(bootstrap?.branches[0]?.id ?? "");
  const [locale, setLocale] = useState<SupportedLocale>("en");
  const [bookingMode, setBookingMode] = useState<"web_page" | "web_app" | "both">("web_page");

  const [editingId, setEditingId] = useState("");
  const [editFullName, setEditFullName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editEmail, setEditEmail] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editLocale, setEditLocale] = useState<SupportedLocale>("en");
  const [editBookingMode, setEditBookingMode] = useState<"web_page" | "web_app" | "both">("web_page");

  const [messageChannel, setMessageChannel] = useState("telegram");
  const [messageMarkdown, setMessageMarkdown] = useState("");
  const [attachmentAssetId, setAttachmentAssetId] = useState("");
  const [uploadingAttachment, setUploadingAttachment] = useState(false);

  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const listKey = useMemo(() => ["manager-clients", search, listMode], [search, listMode]);
  const list = useQuery({
    queryKey: listKey,
    queryFn: () =>
      apiGet<{ items: Customer[] }>(
        `/v3/customers?archived=${listMode === "archived"}${search ? `&search=${encodeURIComponent(search)}` : ""}`
      ),
  });
  const selectedCustomer = useMemo(
    () => (list.data?.items ?? []).find((c) => c.id === selectedId) || null,
    [list.data?.items, selectedId]
  );
  const filteredCustomers = useMemo(() => {
    return (list.data?.items ?? []).filter((c) => {
      if (riskFilter !== "all" && c.risk_tier !== riskFilter) return false;
      if (telegramFilter === "linked" && !c.telegram_chat_id) return false;
      if (telegramFilter === "unlinked" && c.telegram_chat_id) return false;
      return true;
    });
  }, [list.data?.items, riskFilter, telegramFilter]);
  const missingTelegramLink = messageChannel === "telegram" && !!selectedCustomer && !selectedCustomer.telegram_chat_id;

  useEffect(() => {
    const baseLocale = bootstrap?.tenant.default_locale;
    setLocale(normalizeLocale(baseLocale));
  }, [bootstrap?.tenant.default_locale]);

  const risk = useQuery({
    queryKey: ["manager-client-risk", selectedId],
    queryFn: () => apiGet<{ score: number; tier: string; factors: Record<string, number> }>(`/v3/customers/${selectedId}/risk-profile`),
    enabled: !!selectedId,
  });

  const conversation = useQuery({
    queryKey: ["manager-client-conversation", selectedId],
    queryFn: () => apiGet<{ items: ConversationItem[] }>(`/v3/customers/${selectedId}/conversation`),
    enabled: !!selectedId,
  });

  async function uploadChatAttachment(file: File): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("type", "chat");
    const res = await fetch("/v3/branding/assets", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      body: formData,
    });
    if (!res.ok) {
      throw new Error("Attachment upload failed");
    }
    const data = (await res.json()) as UploadOut;
    return data.id;
  }

  const create = useMutation({
    mutationFn: () =>
      apiPost("/v3/customers", {
        branch_id: branchId || null,
        full_name: fullName,
        phone: phone || null,
        email: email || null,
        communication_opt_in: true,
        locale,
        telegram_booking_mode: bookingMode,
      }),
    onSuccess: async () => {
      setFullName("");
      setPhone("");
      setEmail("");
      setLocale(normalizeLocale(bootstrap?.tenant.default_locale));
      setBookingMode("web_page");
      setError("");
      setNotice(tr("manager.clients.notice.created", "Client created."));
      await qc.invalidateQueries({ queryKey: listKey });
    },
    onError: (err) => {
      setError(String(err));
      setNotice("");
    },
  });

  const update = useMutation({
    mutationFn: () =>
      apiPut(`/v3/customers/${editingId}`, {
        branch_id: editBranchId || null,
        full_name: editFullName,
        phone: editPhone || null,
        email: editEmail || null,
        notes: null,
        communication_opt_in: true,
        locale: editLocale,
        telegram_booking_mode: editBookingMode,
        preferred_staff_id: null,
      }),
    onSuccess: async () => {
      setEditingId("");
      setError("");
      setNotice(tr("manager.clients.notice.updated", "Client updated."));
      await qc.invalidateQueries({ queryKey: listKey });
    },
    onError: (err) => {
      setError(String(err));
      setNotice("");
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/customers/${id}`),
    onSuccess: async () => {
      if (editingId) setEditingId("");
      setError("");
      setNotice(tr("manager.clients.notice.archived", "Client archived."));
      await qc.invalidateQueries({ queryKey: listKey });
      await qc.invalidateQueries({ queryKey: ["manager-client-conversation", selectedId] });
    },
    onError: (err) => {
      setError(String(err));
      setNotice("");
    },
  });

  const restore = useMutation({
    mutationFn: (id: string) => apiPost(`/v3/customers/${id}/restore`, {}),
    onSuccess: async () => {
      setError("");
      setNotice(tr("manager.clients.notice.restored", "Client restored."));
      await qc.invalidateQueries({ queryKey: listKey });
    },
    onError: (err) => {
      setError(String(err));
      setNotice("");
    },
  });

  const sendMessage = useMutation({
    mutationFn: () =>
      apiPost(`/v3/customers/${selectedId}/conversation/send`, {
        channel: messageChannel,
        body_markdown: messageMarkdown || null,
        media_asset_id: attachmentAssetId || null,
      }),
    onSuccess: async () => {
      setMessageMarkdown("");
      setAttachmentAssetId("");
      setError("");
      setNotice(tr("manager.clients.notice.sent", "Message sent."));
      await qc.invalidateQueries({ queryKey: ["manager-client-conversation", selectedId] });
    },
    onError: (err) => {
      setError(humanizeSendError(err, selectedCustomer?.phone));
      setNotice("");
      qc.invalidateQueries({ queryKey: ["manager-client-conversation", selectedId] });
    },
  });

  const beginEdit = (c: Customer) => {
    setEditingId(c.id);
    setEditFullName(c.full_name || "");
    setEditPhone(c.phone || "");
    setEditEmail(c.email || "");
    setEditBranchId(c.branch_id || "");
    setEditLocale(c.locale || "en");
    setEditBookingMode(c.telegram_booking_mode || "web_page");
  };

  const templateButtons = [
    {
      label: tr("manager.clients.template.confirmReminder", "Confirm Reminder"),
      markdown:
        "Hi! This is a quick reminder about your upcoming appointment. Reply here if you need to reschedule.",
    },
    {
      label: tr("manager.clients.template.noShow", "No-show Follow-up"),
      markdown:
        "We missed you today. If you want, I can help you book a new time that works better.",
    },
    {
      label: tr("manager.clients.template.reactivation", "Reactivation"),
      markdown:
        "It has been a while since your last visit. We have fresh slots this week if you want to come in.",
    },
  ] as const;

  const openClientBooking = (c: Customer) => {
    if (c.is_archived) return;
    const params = new URLSearchParams({ customer_id: c.id });
    if (c.full_name) params.set("customer_name", c.full_name);
    if (c.branch_id) params.set("branch_id", c.branch_id);
    navigate(`/app/clients/book?${params.toString()}`);
  };

  const repeatLastProcedure = (c: Customer) => {
    if (c.is_archived) return;
    const params = new URLSearchParams({ customer_id: c.id, repeat_last: "1" });
    if (c.full_name) params.set("customer_name", c.full_name);
    if (c.branch_id) params.set("branch_id", c.branch_id);
    navigate(`/app/clients/book?${params.toString()}`);
  };

  return (
    <div className="grid gap-4">
      <header className="manager-ribbon">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-xs uppercase tracking-[0.14em] text-slate-300">{tr("manager.clients.intelligence", "Client Intelligence")}</p>
            <h1 className="text-2xl font-semibold mt-1">{tr("manager.clients.title", "Clients")}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button className={`btn ${listMode === "active" ? "" : "secondary"}`} onClick={() => setListMode("active")}>
              {tr("manager.clients.tab.active", "Active")}
            </button>
            <button className={`btn ${listMode === "archived" ? "" : "secondary"}`} onClick={() => setListMode("archived")}>
              {tr("manager.clients.tab.archived", "Archived")}
            </button>
            <input
              className="input w-full sm:max-w-xs"
              placeholder={tr("manager.clients.searchPlaceholder", "Search by name / phone / email")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <label className="text-xs text-slate-300">{tr("manager.clients.filter.risk", "Risk")}</label>
          <select className="input w-full sm:max-w-[160px]" value={riskFilter} onChange={(e) => setRiskFilter(e.target.value as typeof riskFilter)}>
            <option value="all">{tr("manager.clients.filter.all", "All")}</option>
            <option value="minimal">{tr("manager.clients.risk.minimal", "minimal")}</option>
            <option value="low">{tr("manager.clients.risk.low", "low")}</option>
            <option value="medium">{tr("manager.clients.risk.medium", "medium")}</option>
            <option value="high">{tr("manager.clients.risk.high", "high")}</option>
          </select>
          <label className="text-xs text-slate-300">{tr("manager.clients.filter.telegram", "Telegram")}</label>
          <select
            className="input w-full sm:max-w-[170px]"
            value={telegramFilter}
            onChange={(e) => setTelegramFilter(e.target.value as typeof telegramFilter)}
          >
            <option value="all">{tr("manager.clients.filter.all", "All")}</option>
            <option value="linked">{tr("manager.clients.filter.linked", "Linked")}</option>
            <option value="unlinked">{tr("manager.clients.filter.unlinked", "Unlinked")}</option>
          </select>
          <span className="status-pill queue-ok">{tr("manager.clients.visibleCount", `${filteredCustomers.length} visible`, { count: filteredCustomers.length })}</span>
        </div>
      </header>

      {notice ? <p className="text-sm text-emerald-700">{notice}</p> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="grid xl:grid-cols-2 gap-4">
        <section className="panel p-4">
          <h2 className="text-lg font-semibold">{tr("manager.clients.listTitle", "Client List")}</h2>
          <div className="grid gap-2 mt-3 max-h-[620px] overflow-auto pr-0 sm:pr-1">
            {filteredCustomers.map((c) => (
              <article key={c.id} className={`manager-card ${selectedId === c.id ? "selected" : ""}`}>
                {editingId === c.id ? (
                  <div className="grid gap-2">
                    <input className="input" value={editFullName} onChange={(e) => setEditFullName(e.target.value)} />
                    <div className="grid md:grid-cols-2 gap-2">
                      <input className="input" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder={tr("manager.clients.placeholder.phone", "Phone")} />
                      <input className="input" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} placeholder={tr("manager.clients.placeholder.email", "Email")} />
                    </div>
                    <select className="input" value={editBranchId} onChange={(e) => setEditBranchId(e.target.value)}>
                      <option value="">{tr("manager.clients.noBranch", "No branch")}</option>
                      {(bootstrap?.branches ?? []).map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    <select
                      className="input"
                      aria-label={tr("manager.clients.aria.editLocale", "Edit client locale")}
                      value={editLocale}
                      onChange={(e) => setEditLocale(e.target.value as typeof editLocale)}
                    >
                      {SUPPORTED_LOCALES.map((x) => (
                        <option key={x} value={x}>
                          {tr(`locale.${x}`, x.toUpperCase())}
                        </option>
                      ))}
                    </select>
                    <select
                      className="input"
                      aria-label={tr("manager.clients.aria.editBookingMode", "Edit client booking mode")}
                      value={editBookingMode}
                      onChange={(e) => setEditBookingMode(e.target.value as typeof editBookingMode)}
                    >
                      <option value="web_page">{tr("manager.clients.bookingMode.web_page", "Web page")}</option>
                      <option value="web_app">{tr("manager.clients.bookingMode.web_app", "Web app")}</option>
                      <option value="both">{tr("manager.clients.bookingMode.both", "Both")}</option>
                    </select>
                    <div className="flex flex-wrap gap-2">
                      <button className="btn" onClick={() => update.mutate()} disabled={!editFullName.trim()}>
                        {tr("common.save", "Save")}
                      </button>
                      <button className="btn secondary" onClick={() => setEditingId("")}>
                        {tr("common.cancel", "Cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <button className="text-left w-full" onClick={() => setSelectedId(c.id)}>
                      <p className="font-semibold">{c.full_name}</p>
                      <p className="text-sm text-slate-600">{c.phone || c.email || tr("manager.clients.noContact", "No contact details")}</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className={`status-pill ${c.risk_tier === "high" ? "risk-high" : c.risk_tier === "medium" ? "risk-medium" : "risk-low"}`}>
                          {tr("manager.clients.riskPill", "Risk {{tier}} ({{score}})", { tier: tr(`manager.clients.risk.${c.risk_tier}`, c.risk_tier), score: c.risk_score })}
                        </span>
                        <span className="status-pill">{c.telegram_chat_id ? tr("manager.clients.telegram.linked", "Telegram linked") : tr("manager.clients.telegram.unlinked", "Telegram not linked")}</span>
                        <span className="status-pill">{tr("manager.clients.localePill", "Locale {{locale}}", { locale: c.locale || "en" })}</span>
                        <span className="status-pill">{tr(`manager.clients.bookingMode.${humanizeBookingMode(c.telegram_booking_mode)}`, humanizeBookingMode(c.telegram_booking_mode))}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        {tr("manager.clients.stats", "Visits {{visits}} | Completed {{completed}} | No-show {{noShow}}", {
                          visits: c.visits_total ?? 0,
                          completed: c.completed_total ?? Math.max(0, (c.visits_total ?? 0) - (c.cancelled_total ?? 0) - (c.no_show_total ?? 0)),
                          noShow: c.no_show_total ?? 0,
                        })}
                      </p>
                    </button>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {!c.is_archived ? (
                        <button className="btn" onClick={() => openClientBooking(c)}>
                          {tr("manager.clients.action.assignProcedure", "Assign Procedure")}
                        </button>
                      ) : null}
                      {!c.is_archived ? (
                        <button className="btn secondary" onClick={() => repeatLastProcedure(c)}>
                          {tr("manager.clients.action.repeatLastProcedure", "Repeat Last Procedure")}
                        </button>
                      ) : null}
                      <button className="btn secondary" onClick={() => beginEdit(c)}>
                        {tr("common.edit", "Edit")}
                      </button>
                      {c.is_archived ? (
                        <button className="btn secondary" onClick={() => restore.mutate(c.id)}>
                          {tr("manager.clients.action.restore", "Restore")}
                        </button>
                      ) : (
                        <button className="btn secondary" onClick={() => remove.mutate(c.id)}>
                          {tr("manager.clients.action.archive", "Archive")}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </article>
            ))}
            {!filteredCustomers.length ? <p className="text-sm text-slate-500">{tr("manager.clients.noClients", "No clients found.")}</p> : null}
          </div>

          <div className="mt-4 border-t pt-4">
            <h2 className="text-lg font-semibold">{tr("manager.clients.addTitle", "Add Client")}</h2>
            <div className="grid md:grid-cols-6 gap-2 mt-2">
              <input className="input" placeholder={tr("manager.clients.placeholder.fullName", "Full name")} value={fullName} onChange={(e) => setFullName(e.target.value)} />
              <input className="input" placeholder={tr("manager.clients.placeholder.phone", "Phone")} value={phone} onChange={(e) => setPhone(e.target.value)} />
              <input className="input" placeholder={tr("manager.clients.placeholder.email", "Email")} value={email} onChange={(e) => setEmail(e.target.value)} />
              <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{tr("manager.clients.noBranch", "No branch")}</option>
                {(bootstrap?.branches ?? []).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <select
                className="input"
                aria-label={tr("manager.clients.aria.newLocale", "New client locale")}
                value={locale}
                onChange={(e) => setLocale(e.target.value as typeof locale)}
              >
                {SUPPORTED_LOCALES.map((x) => (
                  <option key={x} value={x}>
                    {tr(`locale.${x}`, x.toUpperCase())}
                  </option>
                ))}
              </select>
              <select
                className="input"
                aria-label={tr("manager.clients.aria.newBookingMode", "New client booking mode")}
                value={bookingMode}
                onChange={(e) => setBookingMode(e.target.value as typeof bookingMode)}
              >
                <option value="web_page">{tr("manager.clients.bookingMode.web_page", "Web page")}</option>
                <option value="web_app">{tr("manager.clients.bookingMode.web_app", "Web app")}</option>
                <option value="both">{tr("manager.clients.bookingMode.both", "Both")}</option>
              </select>
            </div>
            <button className="btn mt-2" onClick={() => create.mutate()} disabled={!fullName.trim()}>
              {tr("manager.clients.action.saveClient", "Save Client")}
            </button>
          </div>
        </section>

        <section className="panel p-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold">{tr("manager.clients.conversationTitle", "Client Conversation")}</h2>
            {selectedCustomer && !selectedCustomer.is_archived ? (
              <div className="flex flex-wrap gap-2">
                <button className="btn" onClick={() => openClientBooking(selectedCustomer)}>
                  {tr("manager.clients.action.assignProcedure", "Assign Procedure")}
                </button>
                <button className="btn secondary" onClick={() => repeatLastProcedure(selectedCustomer)}>
                  {tr("manager.clients.action.repeatLastProcedure", "Repeat Last Procedure")}
                </button>
              </div>
            ) : null}
          </div>
          {!selectedId ? <p className="text-sm mt-2">{tr("manager.clients.selectHint", "Select a client to inspect risk and conversation history.")}</p> : null}
          {selectedId && risk.data ? (
            <div className="mt-2 text-sm rounded border border-slate-200 p-3 bg-slate-50">
              <p>
                {tr("manager.clients.riskSummary", "Score: {{score}} | Tier: {{tier}}", { score: risk.data.score, tier: risk.data.tier })}
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {Object.entries(risk.data.factors || {}).map(([key, value]) => (
                  <span key={key} className="factor-chip">
                    {key}: {Number(value).toFixed(1)}
                  </span>
                ))}
                {!Object.keys(risk.data.factors || {}).length ? <span className="text-xs text-slate-500">{tr("manager.clients.noFactors", "No factors available.")}</span> : null}
              </div>
            </div>
          ) : null}

          {selectedId ? (
            <>
              <div className="conversation-list mt-3 max-h-[360px] overflow-auto border rounded-lg p-2 bg-slate-50">
                {(conversation.data?.items ?? []).map((item) => (
                  <article key={item.id} className={`conversation-item ${item.direction === "outbound" ? "conversation-item-out" : item.direction === "inbound" ? "conversation-item-in" : "conversation-item-system"}`}>
                    <div className="conversation-meta">
                      <span className="conversation-badge">{item.kind}</span>
                      <span className="conversation-badge">{item.channel}</span>
                      {item.integration_name ? <span className="conversation-badge">{item.integration_name}</span> : null}
                      {item.status ? <span className="conversation-badge">{item.status}</span> : null}
                      <span className="conversation-time">{new Date(item.created_at).toLocaleString()}</span>
                    </div>

                    {item.event_type ? <p className="text-sm font-medium">Booking event: {item.event_type}</p> : null}

                    {item.body_markdown ? (
                      <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(item.body_markdown) }} />
                    ) : item.body_text ? (
                      <p className="text-sm whitespace-pre-wrap">{item.body_text}</p>
                    ) : null}

                    {item.media?.length ? (
                      <div className="mt-2 grid gap-2">
                        {item.media.map((m, idx) => (
                          <div key={`${item.id}-${idx}`} className="flex items-center gap-2 text-xs bg-white border rounded p-2">
                            {m.asset_id && (m.mime_type || "").startsWith("image/") ? (
                              <AssetPreview assetId={m.asset_id} token={token} className="h-12 w-12 rounded object-cover border" alt={m.filename || "attachment"} />
                            ) : null}
                            <span>{m.filename || "attachment"}</span>
                            <span className="text-slate-500">{m.mime_type || "file"}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}

                    {item.delivery_error ? <p className="text-xs text-red-600 mt-1">{humanizeDeliveryError(item.delivery_error)}</p> : null}
                  </article>
                ))}
                {conversation.isLoading ? <p className="text-sm">{tr("manager.clients.loadingConversation", "Loading conversation...")}</p> : null}
                {!conversation.isLoading && !conversation.data?.items?.length ? <p className="text-sm">{tr("manager.clients.noHistory", "No history yet.")}</p> : null}
              </div>

              <div className="mt-3 border-t pt-3 grid gap-2">
                <h3 className="font-semibold">{tr("manager.clients.sendMessage", "Send Message")}</h3>
                {missingTelegramLink ? (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                    {tr("manager.clients.telegramLinkHint.before", "Telegram is not linked for this client. Ask them to send")} <code>/start</code> {tr("manager.clients.telegramLinkHint.andThen", "then")}{" "}
                    <code>/register {selectedCustomer?.phone || "CLIENT_PHONE"}</code> {tr("manager.clients.telegramLinkHint.after", "to your bot. If they were linked before, you can still try sending and the server will attempt to recover chat ID from history.")}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  {templateButtons.map((template) => (
                    <button key={template.label} className="btn secondary" onClick={() => setMessageMarkdown(template.markdown)}>
                      {template.label}
                    </button>
                  ))}
                </div>
                <div className="grid md:grid-cols-2 gap-2">
                  <select className="input" value={messageChannel} onChange={(e) => setMessageChannel(e.target.value)}>
                    <option value="telegram">{tr("manager.clients.channel.telegram", "telegram")}</option>
                    <option value="sms">{tr("manager.clients.channel.sms", "sms")}</option>
                    <option value="email">{tr("manager.clients.channel.email", "email")}</option>
                  </select>
                  <input
                    className="input"
                    type="file"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingAttachment(true);
                      try {
                        const id = await uploadChatAttachment(file);
                        setAttachmentAssetId(id);
                      } catch (err) {
                        setError(String(err));
                        setNotice("");
                      } finally {
                        setUploadingAttachment(false);
                      }
                    }}
                  />
                </div>
                {attachmentAssetId ? (
                  <div className="flex items-center gap-2">
                    <AssetPreview assetId={attachmentAssetId} token={token} className="h-12 w-12 rounded object-cover border" alt={tr("manager.clients.attachment", "Attachment")} />
                    <button className="btn secondary" onClick={() => setAttachmentAssetId("")}>{tr("manager.clients.action.removeAttachment", "Remove attachment")}</button>
                  </div>
                ) : null}
                <textarea
                  className="input min-h-[100px]"
                  placeholder={tr("manager.clients.placeholder.message", "Write a message using markdown: **bold**, *italic*, [link](https://...)")}
                  value={messageMarkdown}
                  onChange={(e) => setMessageMarkdown(e.target.value)}
                />
                {messageMarkdown ? (
                  <div className="rounded border p-2 bg-white">
                    <p className="text-xs text-slate-500 mb-1">{tr("manager.clients.preview", "Preview")}</p>
                    <div className="markdown-body" dangerouslySetInnerHTML={{ __html: renderSafeMarkdown(messageMarkdown) }} />
                  </div>
                ) : null}
                <button
                  className="btn"
                  disabled={sendMessage.isPending || uploadingAttachment || (!messageMarkdown.trim() && !attachmentAssetId)}
                  onClick={() => sendMessage.mutate()}
                >
                  {tr("manager.clients.action.send", "Send")}
                </button>
              </div>
            </>
          ) : null}
        </section>
      </div>
    </div>
  );
}


